<template>
  <layout-basic>
    <page-hero
      title="See How IMPROVE Compares"
      lead="An honest comparison to help you choose the right learning platform."
      eyebrow="Compare IMPROVE"
    />

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="compare" />
      </v-container>
    </section>

    <section class="compare-intro">
      <v-container style="max-width: 720px">
        <p>
          IMPROVE is built specifically for growing businesses (typically 5–250 people).
          You get a dedicated learning platform with 130+ ready-made courses, the ability
          to upload your own content, and simple annual pricing per business—covering
          onboarding, compliance and development in one place.
        </p>
        <p>
          Use the comparisons below to see how that differs from HR-suite learning modules
          and compliance library specialists. Then decide what fits your organisation.
        </p>
      </v-container>
    </section>

    <section class="compare-cards-section">
      <v-container>
        <h2 class="font-sora section-heading">Individual comparisons</h2>
        <div class="compare-cards">
          <router-link
            v-for="item in competitors"
            :key="item.to"
            :to="item.to"
            class="compare-card"
          >
            <strong>{{ item.title }}</strong>
            <span>{{ item.blurb }}</span>
            <span class="compare-card__link">Compare →</span>
          </router-link>
        </div>
      </v-container>
    </section>

    <point-grid
      title="Why customers switch"
      :points="whySwitchPoints"
      :columns="2"
      tint
    />

    <section class="compare-crosslinks">
      <v-container style="max-width: 720px">
        <h2 class="font-sora section-heading">Not sure if it’s time to move?</h2>
        <p>
          If your current platform feels complicated, hard to engage people with, or
          expensive for what you actually use, start here.
        </p>
        <router-link to="/why-switch" class="text-link">
          Why switch to IMPROVE →
        </router-link>
      </v-container>
    </section>

    <standard-cta
      title="Compare before you decide."
      lead="Watch the demo or experience IMPROVE for yourself."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { marketingPageMeta } from '@/utils/marketingPage';

export default {
  name: 'Compare',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    StandardCta,
    DemoVideo,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'See How IMPROVE Compares | IMPROVE',
      description:
        'An honest comparison to help you choose the right learning platform for your growing business.',
      path: '/compare',
    });
  },
  data() {
    return {
      competitors: [
        {
          title: 'IMPROVE vs Breathe Learn',
          blurb: 'HR software with a learning module vs a dedicated LMS for SMEs.',
          to: '/compare/breathe-learn',
        },
        {
          title: 'IMPROVE vs BrightHR Learning',
          blurb: 'HR-suite learning vs onboarding, compliance and development in one place.',
          to: '/compare/brighthr',
        },
        {
          title: 'IMPROVE vs iHASCO',
          blurb: 'Compliance library specialist vs a full learning platform with your own content.',
          to: '/compare/ihasco',
        },
        {
          title: 'IMPROVE vs Citation Learning',
          blurb: 'Service-led learning offerings vs simple annual pricing for growing businesses.',
          to: '/compare/citation',
        },
      ],
      whySwitchPoints: [
        'Easier to use',
        'Upload your own courses',
        'Better value',
        'Designed for SMEs',
      ],
    };
  },
};
</script>

<style scoped>
.compare-intro {
  padding: 8px 0 8px;
  text-align: center;
}

.compare-intro p {
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 12px;
}

.compare-cards-section {
  padding: 24px 0 40px;
}

.section-heading {
  text-align: center;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 24px;
}

.compare-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  max-width: 980px;
  margin: 0 auto;
}

.compare-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 22px 18px;
  text-decoration: none;
  color: var(--brand-navy, #002458);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.compare-card:hover {
  border-color: var(--brand-teal, #08a0a8);
  box-shadow: 0 8px 24px rgba(0, 36, 88, 0.08);
  color: var(--brand-navy, #002458);
}

.compare-card strong {
  font-size: 17px;
}

.compare-card span {
  color: var(--brand-grey, #6b7280);
  font-size: 14px;
  line-height: 1.45;
}

.compare-card__link {
  margin-top: 8px;
  color: var(--brand-teal, #08a0a8) !important;
  font-weight: 700;
  font-size: 14px;
}

.compare-crosslinks {
  padding: 40px 0 48px;
  text-align: center;
  background: #f7f9fb;
}

.compare-crosslinks p {
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  line-height: 1.55;
  margin: 0 0 16px;
}

.text-link {
  color: var(--brand-teal, #08a0a8);
  font-weight: 700;
  text-decoration: none;
  font-size: 16px;
}

.text-link:hover {
  text-decoration: underline;
}

@media screen and (max-width: 900px) {
  .compare-cards {
    grid-template-columns: 1fr;
  }

  .section-heading {
    font-size: 24px;
  }
}
</style>
